/**
 * State for My Saved Views (#1144): the list, the applied view, and the
 * mutations. Shared by the Legislation page and the workspace Bill Tracking
 * tab so both surfaces stay identical in behaviour.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { SavedView } from '../types/savedViews';
import { isViewAvailableInWorkspace } from '../types/savedViews';
import {
  fetchSavedViews,
  deleteSavedView,
  setPrimarySavedView,
} from '../services/savedViewsService';

export interface UseSavedViewsOptions {
  /**
     Restrict the offered views to those usable in this workspace. Set on a
     workspace's Bill Tracking tab; left unset on the Legislation page, which is
     the org-level surface listing everything the user has.
  */
  workspaceId?: string;
  /** The workspace's owning org — bounds org-wide (no-workspace) views to their own org. */
  workspaceOwnerOrgId?: string;
}

export function useSavedViews({ workspaceId, workspaceOwnerOrgId }: UseSavedViewsOptions = {}) {
  /*
    Mutations operate on the FULL list — deleting or re-starring a view the
    current workspace does not offer must still behave — while everything the
    UI reads comes from the scoped view below.
  */
  const [allViews, setViews] = useState<SavedView[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);

  const views = useMemo(
    () =>
      workspaceId
        ? allViews.filter((v) => isViewAvailableInWorkspace(v, workspaceId, workspaceOwnerOrgId))
        : allViews,
    [allViews, workspaceId, workspaceOwnerOrgId]
  );

  const refresh = useCallback(async () => {
    try {
      const list = await fetchSavedViews();
      setViews(list);
      setLoaded(true);
      return list;
    } catch {
      // Non-fatal: the table works without views.
      setLoaded(true);
      return [] as SavedView[];
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const remove = useCallback(
    async (view: SavedView) => {
      try {
        await deleteSavedView(view.id);
        setViews((prev) => prev.filter((v) => v.id !== view.id));
        setActiveViewId((prev) => (prev === view.id ? null : prev));
        toast.success(`Deleted "${view.name}"`);
      } catch {
        toast.error('Failed to delete the view');
      }
    },
    []
  );

  const makePrimary = useCallback(async (view: SavedView) => {
    try {
      const updated = await setPrimarySavedView(view.id, true);
      setViews((prev) =>
        prev.map((v) =>
          v.id === updated.id ? updated : { ...v, isPrimary: false }
        )
      );
      toast.success(`"${view.name}" is now your primary view`);
    } catch {
      toast.error('Failed to set the primary view');
    }
  }, []);

  /**
   * Unset the current primary so the built-in Default View is what loads —
   * "make Default primary" is the absence of a primary row, not a row.
   */
  const clearPrimary = useCallback(async () => {
    // Full list: the primary may be one this workspace does not offer.
    const current = allViews.find((v) => v.isPrimary);
    if (!current) return;
    try {
      await setPrimarySavedView(current.id, false);
      setViews((prev) => prev.map((v) => ({ ...v, isPrimary: false })));
      toast.success('Default View will now load by default');
    } catch {
      toast.error('Failed to update the default');
    }
  }, [allViews]);

  /** Insert or replace a view after the wizard saves, keeping primary-first order. */
  const upsert = useCallback((saved: SavedView) => {
    setViews((prev) => {
      const rest = prev.filter((v) => v.id !== saved.id);
      const cleared = saved.isPrimary ? rest.map((v) => ({ ...v, isPrimary: false })) : rest;
      return [saved, ...cleared].sort(
        (a, b) => Number(b.isPrimary) - Number(a.isPrimary)
      );
    });
  }, []);

  return {
    views,
    loaded,
    activeViewId,
    setActiveViewId,
    refresh,
    remove,
    makePrimary,
    clearPrimary,
    upsert,
    /*
      Scoped deliberately: the primary auto-applies on load, and a primary built
      over other workspaces must not apply itself here. A workspace offering no
      primary of its own simply opens on the Default View.
    */
    primaryView: views.find((v) => v.isPrimary) ?? null,
  };
}
